import React, { useRef, useEffect } from "react";
import { Card, Input, Space, Button, List, Tag, Tooltip, Empty } from "antd";
import {
  RiSearchLine,
  RiUser3Line,
  RiTempHotLine,
  RiHeartPulseLine,
  RiLungsFill,
  RiBattery2Line,
  RiTimeLine,
  RiCheckboxCircleLine,
  RiUserUnfollowLine,
  RiErrorWarningLine,
  RiAlertLine,
  RiTeamLine,
} from "react-icons/ri";
import moment from "moment";

const { Search } = Input;

// Where a position came from (useLiveData's positionMethod). ± is shown only
// where the stored accuracy is in METRES: weighted_centroid stores an RSSI
// error in dB, and the remaining methods store no meaningful spread.
const SOURCE_LABELS = {
  gnss: "GPS",
  uwb_tdoa: "UWB",
  trilateration: "藍牙",
  weighted_centroid: "藍牙",
  linear_interpolation: "藍牙",
  proximity: "藍牙",
};
const METRE_ACCURACY_METHODS = new Set(["gnss", "uwb_tdoa", "trilateration"]);

export const positionSourceTag = (method, accuracy) => {
  const label = SOURCE_LABELS[method];
  if (!label) return null;
  return METRE_ACCURACY_METHODS.has(method) && Number.isFinite(accuracy)
    ? `${label} ±${accuracy.toFixed(1)} m`
    : label;
};

const PersonnelPanel = ({
  personnel,
  selectedPersonnelId,
  onPersonnelClick,
  searchQuery,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
}) => {
  const personnelRefs = useRef({});

  useEffect(() => {
    if (selectedPersonnelId && personnelRefs.current[selectedPersonnelId]) {
      personnelRefs.current[selectedPersonnelId].scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [selectedPersonnelId]);

  useEffect(() => {
    const styleId = "personnel-animations";
    if (!document.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      style.textContent = `
        @keyframes sosBlink {
          0%, 100% {
            opacity: 1;
            transform: scale(1);
          }
          50% {
            opacity: 0.7;
            transform: scale(1.05);
          }
        }
        
        .filter-button {
          transition: all 0.3s ease;
          position: relative;
        }
        
        .filter-button:hover {
          transform: scale(1.05) !important;
        }
        
        .filter-button:active {
          transform: scale(0.95) !important;
        }
      `;
      document.head.appendChild(style);
    }

    return () => {
      const existingStyle = document.getElementById(styleId);
      if (existingStyle) {
        existingStyle.remove();
      }
    };
  }, []);

  const getStatusConfig = (status) => {
    const configs = {
      active: { color: "#52c41a", text: "正常", badge: "success" },
      warning: { color: "#faad14", text: "警告", badge: "warning" },
      sos: { color: "#ff4d4f", text: "緊急", badge: "error" },
      offline: { color: "#8c8c8c", text: "離線", badge: "default" },
    };
    return configs[status] || configs.offline;
  };

  const getTimeAgo = (timestamp) => {
    if (timestamp == null) return "無資料";
    const ts =
      typeof timestamp === "string" ? new Date(timestamp).getTime() : timestamp;
    if (isNaN(ts)) return "無資料";
    const seconds = Math.floor((Date.now() - ts) / 1000);
    if (seconds < 60) return "剛剛";
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} 分鐘前`;
    const hours = Math.floor(minutes / 60);
    return `${hours} 小時前`;
  };

  const getVitalColor = (vital, type) => {
    if (type === "temperature") {
      if (vital >= 37.5) return "#ff4d4f";
      if (vital >= 37.0) return "#faad14";
      return "#52c41a";
    }
    if (type === "heartRate") {
      if (vital >= 100 || vital < 50) return "#ff4d4f";
      if (vital >= 85 || vital < 60) return "#faad14";
      return "#52c41a";
    }
    if (type === "spo2") {
      if (vital < 90) return "#ff4d4f";
      if (vital < 95) return "#faad14";
      return "#52c41a";
    }
    if (type === "battery") {
      if (vital < 20) return "#ff4d4f";
      if (vital < 40) return "#faad14";
      return "#52c41a";
    }
    return "#52c41a";
  };

  return (
    <Card
      title={`值班人員列表 (${personnel.length})`}
      size="small"
      bordered
      style={{ height: "100%", display: "flex", flexDirection: "column" }}
      bodyStyle={{
        flex: 1,
        padding: 12,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          gap: 12,
        }}
      >
        <Search
          placeholder="搜尋人員姓名..."
          prefix={<RiSearchLine />}
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          allowClear
        />

        <Space wrap size={8}>
          <Button
            className="filter-button"
            size="small"
            type={statusFilter === "all" ? "primary" : "default"}
            onClick={() => onStatusFilterChange("all")}
            icon={<RiTeamLine style={{ fontSize: 16 }} />}
            style={{
              fontWeight: statusFilter === "all" ? "600" : "normal",
              background:
                statusFilter === "all"
                  ? "linear-gradient(135deg, #1890ff 0%, #096dd9 100%)"
                  : undefined,
              borderColor: statusFilter === "all" ? "#1890ff" : undefined,
              color: statusFilter === "all" ? "#fff" : undefined,
              boxShadow:
                statusFilter === "all"
                  ? "0 4px 12px rgba(24, 144, 255, 0.4), 0 0 0 3px rgba(24, 144, 255, 0.1)"
                  : undefined,
            }}
          >
            全部
          </Button>
          <Button
            className="filter-button"
            size="small"
            type={statusFilter === "active" ? "primary" : "default"}
            onClick={() => onStatusFilterChange("active")}
            icon={<RiCheckboxCircleLine style={{ fontSize: 16 }} />}
            style={{
              fontWeight: statusFilter === "active" ? "600" : "normal",
              background:
                statusFilter === "active"
                  ? "linear-gradient(135deg, #52c41a 0%, #389e0d 100%)"
                  : undefined,
              borderColor: statusFilter === "active" ? "#52c41a" : undefined,
              color: statusFilter === "active" ? "#fff" : undefined,
              boxShadow:
                statusFilter === "active"
                  ? "0 4px 12px rgba(82, 196, 26, 0.5), 0 0 0 3px rgba(82, 196, 26, 0.15)"
                  : undefined,
            }}
          >
            在線
          </Button>
          <Button
            className="filter-button"
            size="small"
            type={statusFilter === "offline" ? "primary" : "default"}
            onClick={() => onStatusFilterChange("offline")}
            icon={<RiUserUnfollowLine style={{ fontSize: 16 }} />}
            style={{
              fontWeight: statusFilter === "offline" ? "600" : "normal",
              background:
                statusFilter === "offline"
                  ? "linear-gradient(135deg, #8c8c8c 0%, #595959 100%)"
                  : undefined,
              borderColor: statusFilter === "offline" ? "#8c8c8c" : undefined,
              color: statusFilter === "offline" ? "#fff" : undefined,
              boxShadow:
                statusFilter === "offline"
                  ? "0 4px 12px rgba(140, 140, 140, 0.5), 0 0 0 3px rgba(140, 140, 140, 0.15)"
                  : undefined,
            }}
          >
            離線
          </Button>
          <Button
            className="filter-button"
            size="small"
            type={statusFilter === "warning" ? "primary" : "default"}
            onClick={() => onStatusFilterChange("warning")}
            icon={<RiErrorWarningLine style={{ fontSize: 16 }} />}
            style={{
              fontWeight: statusFilter === "warning" ? "600" : "normal",
              background:
                statusFilter === "warning"
                  ? "linear-gradient(135deg, #faad14 0%, #d48806 100%)"
                  : undefined,
              borderColor: statusFilter === "warning" ? "#faad14" : undefined,
              color: statusFilter === "warning" ? "#fff" : undefined,
              boxShadow:
                statusFilter === "warning"
                  ? "0 4px 12px rgba(250, 173, 20, 0.5), 0 0 0 3px rgba(250, 173, 20, 0.15)"
                  : undefined,
            }}
          >
            注意
          </Button>
          <Button
            className="filter-button"
            size="small"
            type={statusFilter === "sos" ? "primary" : "default"}
            onClick={() => onStatusFilterChange("sos")}
            icon={<RiAlertLine style={{ fontSize: 16 }} />}
            style={{
              fontWeight: statusFilter === "sos" ? "600" : "normal",
              background:
                statusFilter === "sos"
                  ? "linear-gradient(135deg, #ff4d4f 0%, #cf1322 100%)"
                  : undefined,
              borderColor: statusFilter === "sos" ? "#ff4d4f" : undefined,
              color: statusFilter === "sos" ? "#fff" : undefined,
              boxShadow:
                statusFilter === "sos"
                  ? "0 4px 12px rgba(255, 77, 79, 0.6), 0 0 0 3px rgba(255, 77, 79, 0.2)"
                  : undefined,
            }}
          >
            警急
          </Button>
        </Space>

        <div
          style={{
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            minHeight: 0,
          }}
        >
          <List
            dataSource={personnel}
            locale={{
              emptyText: (
                <Empty
                  description="無人員資料"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              ),
            }}
            renderItem={(person) => {
              const statusConfig = getStatusConfig(person.status);
              const isSelected = person.id === selectedPersonnelId;
              const sourceTag = positionSourceTag(
                person.positionMethod,
                person.positionAccuracy,
              );

              return (
                <List.Item
                  ref={(el) => {
                    if (el) {
                      personnelRefs.current[person.id] = el;
                    }
                  }}
                  style={{
                    cursor: "pointer",
                    padding: 12,
                    borderRadius: 8,
                    marginBottom: 8,
                    border: `2px solid ${isSelected ? statusConfig.color : "#f0f0f0"}`,
                    backgroundColor: isSelected ? "#f6ffed" : "#fafafa",
                    transition: "all 0.3s",
                  }}
                  onClick={() => onPersonnelClick(person.id)}
                >
                  <Space
                    direction="vertical"
                    size={8}
                    style={{ width: "100%" }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Space>
                        <RiUser3Line
                          style={{ fontSize: 18, color: statusConfig.color }}
                        />
                        <strong style={{ fontSize: 16 }}>{person.name}</strong>
                        {person.status === "sos" && (
                          <Tag
                            color="red"
                            style={{
                              animation: "sosBlink 1.5s ease-in-out infinite",
                              fontWeight: "bold",
                            }}
                          >
                            SOS
                          </Tag>
                        )}
                      </Space>
                      <div
                        style={{
                          fontSize: 12,
                          color: "#8c8c8c",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {sourceTag && (
                          <Tag style={{ marginRight: 6, fontSize: 11 }}>
                            {sourceTag}
                          </Tag>
                        )}
                        <RiTimeLine style={{ marginRight: 4 }} />
                        {getTimeAgo(
                          person.vitals?.lastUpdate ??
                            person.lastPositionUpdate ??
                            person.lastUpdate,
                        )}
                      </div>
                    </div>

                    {(person.company ||
                      person.jobTitle ||
                      person.bloodType) && (
                      <div
                        style={{
                          fontSize: 12,
                          color: "#595959",
                          display: "flex",
                          gap: 8,
                          flexWrap: "wrap",
                        }}
                      >
                        {person.company && (
                          <Tag color="blue" style={{ margin: 0 }}>
                            {person.company}
                          </Tag>
                        )}
                        {person.jobTitle && (
                          <Tag color="cyan" style={{ margin: 0 }}>
                            {person.jobTitle}
                          </Tag>
                        )}
                        {person.bloodType && (
                          <Tag color="red" style={{ margin: 0 }}>
                            {person.bloodType}
                          </Tag>
                        )}
                      </div>
                    )}

                    {(() => {
                      const v = person.vitals;
                      const temp = v?.bodyTemperature ?? v?.temperature;
                      const hr = v?.heartRate;
                      const spo2 = v?.bloodOxygen ?? v?.spo2;
                      const bat = v?.battery;
                      return (
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            fontSize: 12,
                          }}
                        >
                          <Tooltip title="體溫">
                            <span
                              style={{
                                color: temp
                                  ? getVitalColor(
                                      parseFloat(temp),
                                      "temperature",
                                    )
                                  : "#8c8c8c",
                              }}
                            >
                              <RiTempHotLine style={{ marginRight: 2 }} />
                              {temp ? `${temp}°C` : "- -"}
                            </span>
                          </Tooltip>
                          <Tooltip title="心率">
                            <span
                              style={{
                                color: hr
                                  ? getVitalColor(hr, "heartRate")
                                  : "#8c8c8c",
                              }}
                            >
                              <RiHeartPulseLine style={{ marginRight: 2 }} />
                              {hr ? `${hr} bpm` : "- -"}
                            </span>
                          </Tooltip>
                          <Tooltip title="血氧">
                            <span
                              style={{
                                color: spo2
                                  ? getVitalColor(spo2, "spo2")
                                  : "#8c8c8c",
                              }}
                            >
                              <RiLungsFill style={{ marginRight: 2 }} />
                              {spo2 ? `${spo2}%` : "- -"}
                            </span>
                          </Tooltip>
                          <Tooltip title="電量">
                            <span
                              style={{
                                color: bat
                                  ? getVitalColor(bat, "battery")
                                  : "#8c8c8c",
                              }}
                            >
                              <RiBattery2Line style={{ marginRight: 2 }} />
                              {bat ? `${bat}%` : "- -"}
                            </span>
                          </Tooltip>
                        </div>
                      );
                    })()}
                  </Space>
                </List.Item>
              );
            }}
          />
        </div>
      </div>
    </Card>
  );
};

export default PersonnelPanel;
